import React, { forwardRef, InputHTMLAttributes, SelectHTMLAttributes } from 'react';

export type MaskType = 'cpf' | 'cnpj' | 'phone' | 'cep' | 'currency' | 'none';

// Define a base props interface that includes the shared onChange handler
interface CommonInputProps {
  label?: string;
  error?: string;
  icon?: React.ReactNode;
  containerClassName?: string;
  children?: React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

// Props for when the component is an input
interface BaseInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> {
  as?: 'input';
  mask?: MaskType;
}

// Props for when the component is a select
interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'onChange'> {
  as: 'select';
  mask?: never; // mask is not applicable to select
}

// Union type for all possible props
export type CombinedInputProps = CommonInputProps & (BaseInputProps | SelectProps);

const Input = forwardRef<HTMLInputElement | HTMLSelectElement, CombinedInputProps>(
  (
    {
      label,
      error,
      icon,
      containerClassName = '',
      className = '',
      as = 'input',
      children,
      onChange,
      ...props
    },
    ref
  ) => {
    const baseClasses =
      'w-full px-4 py-2 border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed';

    const errorClasses = 'border-red-500 focus:ring-red-500';
    const normalClasses = 'border-gray-300 focus:ring-blue-500';
    const iconClasses = icon ? 'pl-10' : '';

    const combinedClassName = `${baseClasses} ${error ? errorClasses : normalClasses} ${iconClasses} ${className}`;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (as === 'input' && 'mask' in props && props.mask !== 'none') {
        const inputElement = e.target as HTMLInputElement;
        const maskedValue = applyMask(inputElement.value, props.mask as MaskType);
        inputElement.value = maskedValue;
      }
      onChange?.(e);
    };

    const applyMask = (value: string, maskType: MaskType): string => {
      const cleaned = value.replace(/\D/g, '');
      switch (maskType) {
        case 'cpf':
          return cleaned
            .slice(0, 11)
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        case 'phone':
          if (cleaned.length <= 10) {
            return cleaned.slice(0, 10).replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d{1,4})$/, '$1-$2');
          }
          return cleaned.slice(0, 11).replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d{1,4})$/, '$1-$2');
        case 'cep':
          return cleaned.slice(0, 8).replace(/(\d{5})(\d{1,3})$/, '$1-$2');
        default: return value;
      }
    };

    const renderInput = () => (
      <input
        ref={ref as React.Ref<HTMLInputElement>}
        className={combinedClassName}
        onChange={handleChange}
        {...(props as Omit<BaseInputProps, 'as'>)}
      />
    );

    const renderSelect = () => (
      <select
        ref={ref as React.Ref<HTMLSelectElement>}
        className={combinedClassName}
        onChange={handleChange}
        {...(props as Omit<SelectProps, 'as'>)}
      >
        {children}
      </select>
    );

    return (
      <div className={`w-full ${containerClassName}`}>
        {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
        <div className="relative">
          {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">{icon}</div>}
          {as === 'select' ? renderSelect() : renderInput()}
        </div>
        {error && <p className="mt-1 text-sm text-red-600 animate-fade-in">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
